import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, BlockStack, Button, Card, Collapsible, Form, FormLayout, IndexTable, Link, Popover, Text, TextField } from '@shopify/polaris';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import AccountingInvoicePreviewModal, { PreviewInvoiceData } from 'entities/invoices/components/AccountingInvoicePreviewModal';
import { TypedTripManifest, useMutateUpdateTrip_manifest } from 'queries/trip_manifest.query';
import helpers from 'helpers/index';
import { useField, useForm } from '@shopify/react-form';
import __ from 'languages/index';
import { useMutateVehicleLogSync } from 'queries/vehicle_log.query';
import {
  ENUM_INVOICE_STATUS,
  useMutateDeleteInvoices,
  useMutateExportInvoices,
  useMutatePreviewInvoices,
  useMutateSignInvoices,
} from 'queries/invoices.query';
import { useGetInvoices } from 'queries/invoices.query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faLink } from '@fortawesome/free-solid-svg-icons';

const LOCAL_STORAGE_KEYS = {
  vehicleLogAndManifestOpen: 'AccountingShell.vehicleLogAndManifestOpen',
  invoicesOpen: 'AccountingShell.invoicesOpen',
} as const;

const getStoredCollapseState = (key: string) => {
  if (typeof window === 'undefined') return false;

  return window.localStorage.getItem(key) === 'true';
};

const setStoredCollapseState = (key: string, value: boolean) => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(key, String(value));
};

export default function AccountingShell({ tripManifestData }: { tripManifestData: TypedTripManifest }) {
  // const [allowExportInvoice, setAllowExportInvoice] = useState(false);

  const { mutateAsync: vehicleLogSync, isPending: loadingSyncVehicleLog } = useMutateVehicleLogSync();
  const vehicleLogSyncCallback = useCallback(async (trip_manifest_id: any) => {
    try {
      await vehicleLogSync(trip_manifest_id);
    } catch (e) {}
  }, []);

  // useEffect(() => {
  //   if (!tripManifestData) return;
  //   let ALLOWED = true;
  //   if (tripManifestData?.total_occupied_seats < 1) {
  //     /** vé gửi hàng không tính vào số ghế, cho nên phải khóa lại kẻo 1 chuyến có chỉ 1 hàng thì không hoạt động */
  //     // ALLOWED = false;
  //   }
  //   if (tripManifestData?.total_fare < 1) {
  //     ALLOWED = false;
  //   }
  //   if (tripManifestData?.is_exported_invoice === 1) {
  //     ALLOWED = false;
  //   }
  //   setAllowExportInvoice(ALLOWED);
  // }, [tripManifestData]);

  const { data: tripManifestInvoices, isLoading: isLoadingInvoices } = useGetInvoices({
    order_id: tripManifestData?.orders?.map((o) => o.id).toString(),
  });
  const { mutateAsync: deleteInvoiceMutation, isPending: DeleteLoading } = useMutateDeleteInvoices();
  const { mutateAsync: signInvoiceMutation, isPending: SignLoading } = useMutateSignInvoices();
  const { mutateAsync: exportInvoiceMutation, isPending: ExportLoading } = useMutateExportInvoices();

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewInvoiceData[] | null>(null);
  const [previewRequest, setPreviewRequest] = useState<any>(null);

  const formatInvoiceDate = useCallback((createdAt?: string) => {
    if (!createdAt) return '-';
    const parsed = new Date(Number(createdAt));
    if (Number.isNaN(parsed.getTime())) return createdAt;
    return parsed.toLocaleString('vi-VN');
  }, []);

  const invoicesWithTools = useMemo(() => {
    if (!tripManifestInvoices || __helpers.isEmpty(tripManifestInvoices)) return [];

    return tripManifestInvoices?.body?.records.map((tripmanifestInvoice) => {
      const isDraft = tripmanifestInvoice?.LoaiHDon === 0;
      const isSigned = tripmanifestInvoice?.is_signed === 1;
      return {
        ...tripmanifestInvoice,
        isDraft,
        isSigned,
      };
    });
  }, [tripManifestInvoices]);

  const { addNotification } = useNotification();
  const { showDeleteConfirm } = useDeleteConfirm();

  const { mutateAsync: getPreviewData, isPending: loadingPreview } = useMutatePreviewInvoices();
  const getPreviewDataCallback = useCallback(
    async (trip_manifest_id) => {
      try {
        const response = await getPreviewData(trip_manifest_id);
        setPreviewRequest(trip_manifest_id);
        setPreviewData(response);
        setPreviewModalOpen(true);
      } catch (e) {}
    },
    [addNotification, getPreviewData]
  );

  const handleExportInvoice = useCallback(async () => {
    if (!previewRequest) return;
    try {
      await exportInvoiceMutation(previewRequest);
      addNotification('info', 'Tạo hóa đơn thành công!');
      setPreviewModalOpen(false);
    } catch (e) {}
  }, [addNotification, exportInvoiceMutation, previewRequest]);

  const [currentDeletingInvoice, setCurrentDeletingInvoice] = useState(null);
  const [currentSignInvoice, setCurrentSignInvoice] = useState(null);
  const deleteInvoiceCallback = useCallback(async (invoice_id: any) => {
    const confirmed = await showDeleteConfirm(
      'Xóa hóa đơn này?',
      'BẠN ĐANG XÓA HÓA ĐƠN! Chỉ có hóa đơn nháp mới được phép xóa! Hành động này không thể khôi phục. '
    );
    if (!confirmed) return;
    setCurrentDeletingInvoice(invoice_id);
    try {
      await deleteInvoiceMutation(invoice_id);
      addNotification('info', 'Xóa hóa đơn thành công!');
    } catch (e) {
      addNotification('error', 'Không thể xóa hóa đơn này!');
    } finally {
      setCurrentDeletingInvoice(null);
    }
  }, []);

  const signInvoiceCallback = useCallback(async (tripManifestInvoiceData: any) => {
    const confirmed = await showDeleteConfirm(
      'Ký hóa đơn này?',
      'BẠN ĐANG KÝ MỘT HÓA ĐƠN! Bạn nên nhớ, hành động này không thể hoàn tác, việc hoàn tác phải được LẬP BIÊN BẢN. HÃY KIỂM TRA KỸYYYYYYYYYYYYY',
      {
        customAction: {
          content: 'Xem lại',
          onAction: () => {
            window.open(tripManifestInvoiceData.urlDownloadPDF, '_blank');
          },
        },
      }
    );
    if (!confirmed) return;

    setCurrentSignInvoice(tripManifestInvoiceData.invoice_id);
    try {
      await signInvoiceMutation(tripManifestInvoiceData.invoice_id);
      addNotification('info', 'Ký hóa đơn thành công!');
    } catch (e) {
      // addNotification('error', 'Không thể ký hóa đơn này!');
    } finally {
      setCurrentSignInvoice(null);
    }
  }, []);

  const rowMarkup = invoicesWithTools?.map((tripManifestInvoiceData, index) => {
    const displayId =
      tripManifestInvoiceData?.maTraCuu || tripManifestInvoiceData?.maSoHDon || tripManifestInvoiceData?.invoice_id || `invoice-${index + 1}`;
    const statusBadge = tripManifestInvoiceData.isDraft ? (
      <Badge>Chưa chính thức</Badge>
    ) : (
      <Badge tone="critical-strong" progress="complete">
        Chính thức
      </Badge>
    );
    const signedBadge = tripManifestInvoiceData.isSigned ? (
      <Badge tone="attention-strong" progress="complete">
        Đã ký
      </Badge>
    ) : (
      <Badge>Chưa ký</Badge>
    );
    const canDelete = !tripManifestInvoiceData.isSigned;
    const canSign = !tripManifestInvoiceData.isSigned;
    const isDeleteLoading = DeleteLoading && currentDeletingInvoice === tripManifestInvoiceData.invoice_id;
    const isSignLoading = SignLoading && currentSignInvoice === tripManifestInvoiceData.invoice_id;
    const manyOrdersHash = String(tripManifestInvoiceData?.orders?.order_code || '');

    return (
      <IndexTable.Row id={tripManifestInvoiceData.invoice_id || displayId} key={displayId} position={index}>
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="bold" as="span">
            {tripManifestInvoiceData.invoice_status === ENUM_INVOICE_STATUS.CANCEL ? (
              tripManifestInvoiceData.invoice_id
            ) : (
              <Link url={process.env.REACT_APP_BACKEND_URL + `/invoices/view_pdf/${tripManifestInvoiceData.maTraCuu}` || '#'} target="_blank">
                {tripManifestInvoiceData.invoice_id}
              </Link>
            )}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {tripManifestInvoiceData.invoice_status === ENUM_INVOICE_STATUS.CANCEL ? (
            <Text as="p">
              {tripManifestInvoiceData.shDon} / {manyOrdersHash}
            </Text>
          ) : (
            <Link
              onClick={() => {
                window.location.hash = String(tripManifestInvoiceData?.order_id);
              }}
            >
              {tripManifestInvoiceData.shDon} / {manyOrdersHash}
            </Link>
          )}
        </IndexTable.Cell>
        <IndexTable.Cell>{tripManifestInvoiceData?.mccqt || '--'}</IndexTable.Cell>
        <IndexTable.Cell>{helpers.formatNumber(tripManifestInvoiceData?.TgTTTBSo)}</IndexTable.Cell>
        <IndexTable.Cell>{tripManifestInvoiceData.TaxType}</IndexTable.Cell>
        <IndexTable.Cell>{helpers.trimMiddleString(tripManifestInvoiceData.NMua_HVTNMHang || tripManifestInvoiceData.NMua_Ten)}</IndexTable.Cell>
        <IndexTable.Cell>{statusBadge}</IndexTable.Cell>
        <IndexTable.Cell>{formatInvoiceDate(tripManifestInvoiceData.createdAt)}</IndexTable.Cell>
        <IndexTable.Cell>{signedBadge}</IndexTable.Cell>
        <IndexTable.Cell>
          {canDelete && (
            <Button tone="critical" loading={isDeleteLoading} onClick={() => deleteInvoiceCallback(tripManifestInvoiceData.invoice_id)}>
              Xóa
            </Button>
          )}
          &nbsp;
          {canSign && (
            <Button tone="success" loading={isSignLoading} onClick={() => signInvoiceCallback(tripManifestInvoiceData)}>
              Ký và phát hành
            </Button>
          )}
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  /** Nhật trình */
  const [openCollapse, setOpenCollapse] = useState(() => getStoredCollapseState(LOCAL_STORAGE_KEYS.vehicleLogAndManifestOpen));
  const handleToggleOpenCollapse = useCallback(() => setOpenCollapse((openCollapse) => !openCollapse), []);
  /** Bảng kê */
  const [openCollapse2, setOpenCollapse2] = useState(() => getStoredCollapseState(LOCAL_STORAGE_KEYS.invoicesOpen));
  const handleToggleOpenCollapse2 = useCallback(() => setOpenCollapse2((openCollapse) => !openCollapse), []);

  useEffect(() => {
    setStoredCollapseState(LOCAL_STORAGE_KEYS.vehicleLogAndManifestOpen, openCollapse);
  }, [openCollapse]);

  useEffect(() => {
    setStoredCollapseState(LOCAL_STORAGE_KEYS.invoicesOpen, openCollapse2);
  }, [openCollapse2]);

  return (
    <BlockStack gap="400">
      <Button
        disclosure={openCollapse ? 'up' : 'down'}
        onClick={handleToggleOpenCollapse}
        ariaExpanded={openCollapse}
        ariaControls="vehicle-log-collapsible"
      >
        Nhật trình và bảng kê
      </Button>
      <Collapsible open={openCollapse} id="vehicle-log-collapsible" transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}>
        <Card>
          <BlockStack gap="400">
            <Text as="p" tone="subdued">
              Sau khi xuất hết vé, Nhật trình và Hóa đơn sẽ gắn liền với xe{' '}
              <Link url={`/vehicle/${tripManifestData?.vehicle_id}`}>
                <strong>{tripManifestData?.vehicle?.license_plate}</strong>
              </Link>
              , vui lòng không chỉnh sửa sau khi xuất hóa đơn.
            </Text>
            <br />

            {tripManifestData?.vehicle_log?.id ? (
              <Link url={`/vehicle/${tripManifestData?.vehicle_id}/detail/${tripManifestData?.vehicle_log?.id}`} removeUnderline>
                <FontAwesomeIcon icon={faLink} /> Xem nhật trình và bảng kê chi tiết
              </Link>
            ) : (
              <Button
                variant="secondary"
                loading={loadingSyncVehicleLog}
                disabled={!tripManifestData?.vehicle_id || tripManifestData?.trip_manifest_driver?.length < 1}
                onClick={async () => {
                  vehicleLogSyncCallback(tripManifestData.id);
                }}
              >
                Tạo nhật trình và bảng kê
              </Button>
            )}
          </BlockStack>
        </Card>
      </Collapsible>

      <Button
        fullWidth
        disclosure={openCollapse2 ? 'up' : 'down'}
        onClick={handleToggleOpenCollapse2}
        ariaExpanded={openCollapse2}
        ariaControls="invoices-collapsible"
      >
        Hóa đơn
      </Button>

      <Collapsible open={openCollapse2} id="invoices-collapsible" transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}>
        {Number(tripManifestData?.total_occupied_seats || 0) + Number(tripManifestData?.total_occupied_goods || 0) > 0 ? (
          <Card>
            <BlockStack gap="400">
              <>
                <AccountingInvoicePreviewModal
                  open={previewModalOpen}
                  data={previewData}
                  loading={ExportLoading}
                  onClose={() => setPreviewModalOpen(false)}
                  onCreateInvoice={handleExportInvoice}
                />
                {tripManifestInvoices?.totalItems > 0 ? (
                  <IndexTable
                    resourceName={{
                      singular: 'Hóa đơn',
                      plural: 'Hóa đơn',
                    }}
                    emptyState={<p>Chưa thấy hóa đơn nào!</p>}
                    headings={[
                      { title: 'ID' },
                      { title: 'Số hóa đơn' },
                      { title: 'Mã CQT' },
                      { title: 'Tổng tiền' },
                      { title: 'Loại HD' },
                      { title: 'Ng nhận' },
                      { title: 'Tình trạng' },
                      { title: 'Ngày' },
                      { title: 'Ký' },
                      { title: 'Công cụ' },
                    ]}
                    itemCount={tripManifestInvoices?.totalItems || 0}
                    selectable={false}
                    loading={isLoadingInvoices}
                  >
                    {rowMarkup}
                  </IndexTable>
                ) : (
                  <Text as="p" tone="subdued">
                    Chưa có một hóa đơn nào!
                  </Text>
                )}

                <Button
                  variant="secondary"
                  loading={loadingPreview}
                  onClick={async () => {
                    setPreviewRequest(tripManifestData.id);
                    getPreviewDataCallback(tripManifestData.id);
                  }}
                >
                  Xem trước hóa đơn &amp; tạo
                </Button>
              </>
            </BlockStack>
          </Card>
        ) : (
          <Text as="p" tone="subdued">
            Chuyến này không có đơn hàng nào!
          </Text>
        )}
      </Collapsible>
    </BlockStack>
  );
}

